import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Send, Loader2, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useFieldDraft } from '../hooks/useFieldDraft';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatPhoneDisplay(e164) {
  if (!e164) return '';
  const m = String(e164).match(/^\+1?(\d{3})(\d{3})(\d{4})$/);
  if (m) return `(${m[1]}) ${m[2]}-${m[3]}`;
  return e164;
}

/**
 * SmsComposeModal — lightweight quick-send for outbound SMS.
 *
 * Used from personnel pages (and anywhere else that has a phone in hand).
 * Shows a From-number dropdown populated from /sms/config so admins can
 * choose which Twilio line the message comes from. The last choice is
 * remembered in localStorage.
 *
 * Props:
 *   open: boolean
 *   onClose: fn()
 *   toNumber: string — recipient phone (any format; backend normalizes)
 *   recipientName: string — display name above the form
 *   onSent: fn(result)
 */
export default function SmsComposeModal({ open, onClose, toNumber = '', recipientName = '', onSent }) {
  const [config, setConfig] = useState(null);
  const [fromNumber, setFromNumber] = useState('');
  const [to, setTo] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  // Preserve in-flight body across timeouts so admins don't lose their text.
  const draftKey = open ? `sms-compose:quick:${toNumber}` : null;
  const { clearDraft } = useFieldDraft(draftKey, body, setBody);

  useEffect(() => {
    if (!open) return;
    setTo(toNumber || '');
    // Reset body unless a draft exists for this recipient — useFieldDraft
    // will rehydrate it.
    setBody('');
    axios.get(`${API}/sms/config`).then(res => {
      setConfig(res.data);
      const last = (() => { try { return window.localStorage.getItem('sms:lastFromNumber'); } catch { return null; } })();
      const numbers = res.data?.numbers || [];
      const pick = (last && numbers.find(n => n.phone_number === last)) || numbers[0];
      if (pick) setFromNumber(pick.phone_number);
    }).catch(() => {
      setConfig({ configured: false, numbers: [] });
    });
  }, [open, toNumber]);

  useEffect(() => {
    if (!open || !fromNumber) return;
    try { window.localStorage.setItem('sms:lastFromNumber', fromNumber); } catch {}
  }, [fromNumber, open]);

  if (!open) return null;

  const charsLeft = 160 - body.length;
  const isLong = body.length > 160;

  const handleSend = async () => {
    if (!to.trim()) { toast.error('Recipient phone is required'); return; }
    if (!fromNumber) { toast.error('Pick a from-number'); return; }
    if (!body.trim()) { toast.error('Message body is required'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/sms/send`, {
        from_number: fromNumber,
        to_number: to,
        body: body,
      });
      toast.success('Text sent');
      clearDraft();
      onSent?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(`Send failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const notConfigured = config && !config.configured;
  const noNumbers = config?.configured && (!config.numbers || config.numbers.length === 0);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="sms-compose-modal">
      <div className="bg-white w-full md:max-w-lg md:rounded-xl shadow-2xl flex flex-col max-h-[95vh] overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <MessageCircle size={16} className="text-primary" />
            Send Text{recipientName ? ` — ${recipientName}` : ''}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="sms-compose-close-btn">
            <X size={18} />
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {notConfigured && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              Twilio isn't configured yet. Add SMS credentials in backend settings to send texts.
            </div>
          )}
          {noNumbers && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              No Twilio phone numbers are connected to this account.
            </div>
          )}

          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground w-12">From</label>
            <select
              value={fromNumber}
              onChange={e => setFromNumber(e.target.value)}
              disabled={sending || !config?.numbers?.length}
              data-testid="sms-compose-from"
              className="flex-1 h-9 px-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {(config?.numbers || []).map(n => (
                <option key={n.sid} value={n.phone_number}>
                  {formatPhoneDisplay(n.phone_number)}{n.friendly_name && n.friendly_name !== n.phone_number ? ` — ${n.friendly_name}` : ''}
                </option>
              ))}
              {(!config?.numbers || config.numbers.length === 0) && <option value="">— no numbers available —</option>}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground w-12">To</label>
            <Input
              value={to}
              onChange={e => setTo(e.target.value)}
              placeholder="+15555551234"
              data-testid="sms-compose-to"
              className="flex-1"
            />
          </div>

          <Textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Write your message…"
            className="min-h-[140px] resize-y font-sans text-sm"
            data-testid="sms-compose-body"
          />
          <p className={`text-xs text-right ${isLong ? 'text-amber-700' : 'text-muted-foreground'}`}>
            {body.length} characters{isLong ? ` · will be sent as ${Math.ceil(body.length / 153)} segments` : ` · ${charsLeft} left`}
          </p>
        </div>

        <footer className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-muted/30">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || notConfigured || noNumbers}
            data-testid="sms-compose-send-btn"
            className="gap-2"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? 'Sending…' : 'Send Text'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
